import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useRef } from "react";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import styles from "../../css/CreateOrder.module.css";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const CreateOrderModal = ({ show, handleClose, refreshTable }) => {
  const courierServiceKey = sessionStorage.getItem("courierServiceKey");
  //   console.log("::courierServiceKey::", courierServiceKey);
  const loggedInUserName = sessionStorage.getItem("username");

  const [carrierOptions, setCarrierOptions] = useState([]);

  const [form, setForm] = useState({
    consigneeName: "",
    consigneeMobile: "",
    consigneePhone: "",
    consigneeEmailId: "",
    consigneeAddress1: "",
    consigneePincode: "",
    consigneeCity: "",
    consigneeState: "",
    consigneeCountry: "",
    paymentMode: "",
    expressType: "",
    isNdd: "0",
    orderAmount: "",
    codAmount: "",
    invoiceNumber: "",
    shipmentWeight: "",
    shipmentLength: "",
    shipmentWidth: "",
    shipmentHeight: "",
    pickAddressId: "",
    returnAddressId: "",
    carrierId: "",
    sellerId: "",
    ewaybill_number: "", // For Testing Purpose
  });

  const [products, setProducts] = useState([
    {
      productName: "",
      productQuantity: "",
      productValue: "",
      productCategory: "",
      productDescription: "",
      productSku: "",
      productHsnsac: "",
      productTaxper: "",
    },
  ]);

  const [errors, setErrors] = useState({});
  const [productErrors, setProductErrors] = useState([{}]);
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  const lastFetchedPincodeRef = useRef("");

  const fetchPincodeDetails = useCallback(
    debounce(async (pincode) => {
      if (pincode === lastFetchedPincodeRef.current) return;
      lastFetchedPincodeRef.current = pincode;
      if (!pincode) {
        setForm((prev) => ({
          ...prev,
          consigneeCity: "",
          consigneeState: "",
          consigneeCountry: "",
        }));
        return;
      }
      setPincodeLoading(true);
      try {
        const response = await axios.get(
          `https://api.postalpincode.in/pincode/${pincode}`
        );
        if (
          response.data[0].Status === "Success" &&
          response.data[0].PostOffice.length > 0
        ) {
          const { District, State, Country } = response.data[0].PostOffice[0];
          setForm((prev) => ({
            ...prev,
            consigneeCity: District || "",
            consigneeState: State || "",
            consigneeCountry: Country || "",
          }));
          setErrors((prev) => ({
            ...prev,
            consigneePincode: "",
            consigneeCity: District ? "" : "City not found",
            consigneeState: State ? "" : "State not found",
            consigneeCountry: Country ? "" : "Country not found",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            consigneePincode: "Invalid pincode",
            consigneeCity: "City is required",
            consigneeState: "State is required",
            consigneeCountry: "Country is required",
          }));
          setForm((prev) => ({
            ...prev,
            consigneeCity: "",
            consigneeState: "",
            consigneeCountry: "",
          }));
        }
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          consigneePincode: "Failed to fetch pincode details",
          consigneeCity: "City is required",
          consigneeState: "State is required",
          consigneeCountry: "Country is required",
        }));
        setForm((prev) => ({
          ...prev,
          consigneeCity: "",
          consigneeState: "",
          consigneeCountry: "",
        }));
      } finally {
        setPincodeLoading(false);
      }
    }, 500),
    []
  );

  // useEffect(() => {
  //   setForm((prev) => ({ ...prev, expressType: "surface" }));
  // }, []);

  const validateField = (name, value, formContext = form) => {
    switch (name) {
      case "consigneeName": {
        if (!value.trim()) return "Consignee name is required";
        if (value.length > 50)
          return "Consignee name must be at most 50 characters";
        if (!/^[a-zA-Z\s.'-]+$/.test(value))
          return "Only letters, spaces, periods, hyphens allowed";
        return "";
      }
      // case "consigneeMobile":
      //   return /^[6-9]\d{9}$/.test(value)
      //     ? ""
      //     : "Enter a valid 10-digit mobile number";
      case "consigneeMobile": {
        if (!value) return "Mobile number is required";

        // Must be 10 digits starting with 6-9
        if (!/^[6-9]\d{9}$/.test(value)) {
          return "Enter a valid 10-digit mobile number starting with 6-9";
        }

        // Prevent all digits being same (like 9999999999)
        if (/^(\d)\1{9}$/.test(value)) {
          return "Duplicate/repeated mobile numbers are not allowed";
        }

        return "";
      }

      case "consigneePhone":
        return value && !/^[6-9]\d{9}$/.test(value)
          ? "Enter a valid 10-digit phone number"
          : "";
      case "consigneeEmailId":
        if (!value.trim()) return "Email ID is required";
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? "Enter a valid email address"
          : "";
      case "consigneeAddress1":
        return value.trim() ? "" : "Address is required";
      case "consigneePincode":
        if (!/^[1-9][0-9]{5}$/.test(value))
          return "Enter a valid 6-digit pincode";
        return "";
      case "consigneeCity":
        return value.trim() ? "" : "City is required";
      case "consigneeState":
        return value.trim() ? "" : "State is required";
      case "consigneeCountry":
        return value.trim() ? "" : "Country is required";
      case "paymentMode":
        return value ? "" : "Payment mode is required";
      case "expressType":
        return value ? "" : "Express type is required";
      case "orderAmount":
        const amount = parseFloat(value);
        if (!value || isNaN(amount)) return "Order amount is required";
        if (amount <= 0) {
          if (form.paymentMode === "COD") {
            return "For Cash on Delivery, Order Amount must be greater than 0";
          }
          if (form.paymentMode === "PPD") {
            return "For PPD, Order Amount must be greater than 0";
          }
        }
        return "";

      case "codAmount": {
        const trimmed = value.trim();

        if (formContext.paymentMode === "COD") {
          if (!trimmed) return "COD Amount is required";

          if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
            return "Enter a valid COD amount (e.g., 100.00)";
          }

          if (parseFloat(trimmed) <= 0) {
            return "COD Amount must be greater than 0";
          }
        }

        // For PPD mode or empty COD field — no error
        return "";
      }

      case "shipmentWeight": {
        const trimmed = value.trim();

        if (!trimmed) return "Shipment weight is required";

        if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
          return "Enter a valid weight (e.g., 1.50)";
        }

        if (parseFloat(trimmed) <= 0) {
          return "Weight must be greater than 0";
        }

        return "";
      }

      case "shipmentLength": {
        const trimmed = value.trim();
        if (!trimmed) return "Shipment length is required";

        // Validate format like 10.00, 123.45
        if (!/^\d+(\.\d{1,2})?$/.test(trimmed))
          return "Enter a valid shipment length (e.g., 10.00)";

        // Prevent excessive total length (e.g., 999999, 9999.99 etc.)
        if (trimmed.length > 6)
          return "Shipment length must be at most 6 characters";

        // Prevent repeated digits like 111.00 or 222.22
        if (/^(\d)\1+(\.\d{1,2})?$/.test(trimmed))
          return "Repeated digits not allowed (e.g., 111.00)";

        return "";
      }
      case "shipmentWidth": {
        const trimmed = value.trim();
        if (!trimmed) return "Shipment width is required";

        if (!/^\d+(\.\d{1,2})?$/.test(trimmed))
          return "Enter a valid width (e.g., 10.00)";

        if (trimmed.length > 10) return "Shipment width is too long";

        if (/^(\d)\1+(\.\d{1,2})?$/.test(trimmed))
          return "Repeated digits not allowed (e.g., 111.00)";

        return "";
      }

      case "shipmentWeight": {
        const trimmed = value.trim();

        if (!trimmed) return "Shipment weight is required";

        if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
          return "Enter a valid weight (e.g., 1.50)";
        }

        if (parseFloat(trimmed) <= 0) {
          return "Weight must be greater than 0";
        }

        return "";
      }

      case "pickAddressId": {
        const trimmed = value.trim();
        if (!trimmed) return "Pick address ID is required";

        if (!/^\d+$/.test(trimmed)) return "Only whole numbers are allowed";

        if (trimmed.length > 6)
          return "Pick address ID must be at most 6 digits";

        if (/^(\d)\1{4,}$/.test(trimmed))
          return "Repeated digits not allowed (e.g., 11111)";

        return "";
      }

      case "returnAddressId": {
        const trimmed = value.trim();
        if (!trimmed) return "Return address ID is required";

        if (!/^\d+$/.test(trimmed)) return "Only whole numbers are allowed";

        if (trimmed.length > 6)
          return "Return address ID must be at most 6 digits";

        if (/^(\d)\1{4,}$/.test(trimmed))
          return "Repeated digits not allowed (e.g., 11111)";

        return "";
      }

      case "invoiceNumber": {
        const trimmed = value.trim();
        if (!trimmed) return "Invoice Number is required";
        if (trimmed.length > 20)
          return "Invoice Number must be at most 20 characters";
        if (!/^[a-zA-Z0-9\s\-\/]+$/.test(trimmed))
          return "Only letters, numbers, spaces, hyphens, and slashes allowed";
        return "";
      }
      case "pickAddressId": {
        const trimmed = value.trim();
        if (!trimmed) return "Pick address ID is required";
        if (!/^\d+$/.test(trimmed)) return "Only digits are allowed";
        if (trimmed.length > 6)
          return "Pick address ID must be at most 6 digits";
        return "";
      }

      default:
        return "";
    }
  };

  const validateProductField = (name, value) => {
    switch (name) {
      case "productName": {
        const trimmed = value.trim();

        if (!trimmed) return "Product name is required";

        if (trimmed.length > 50)
          return "Product name must be at most 50 characters";

        if (!/^[a-zA-Z0-9\s\-().]+$/.test(trimmed))
          return "Only letters, numbers, spaces, hyphens, dots, and parentheses allowed";

        return "";
      }
      case "productQuantity":
        if (!value.trim()) return "Product Quantity is required";

        if (!value.trim()) return "Product Category is required";

        if (!/^[A-Za-z0-9]{1,10}$/.test(value)) {
          return "Only letters and numbers allowed (max 10 characters)";
        }

        if (/^([A-Za-z0-9])\1{4,}$/.test(value)) {
          return "Repeated characters are not allowed";
        }

      case "productValue":
        return value && /^\d+(\.\d{1,2})?$/.test(value)
          ? ""
          : "Enter a valid value (e.g., 100.00)";
      case "productCategory":
        if (!value.trim()) return "Product Category is required";

        if (!/^[A-Za-z0-9]{1,10}$/.test(value)) {
          return "Only letters and numbers allowed (max 10 characters)";
        }

        if (/^([A-Za-z0-9])\1{4,}$/.test(value)) {
          return "Repeated characters are not allowed";
        }

        return "";

      case "productDescription": {
        const trimmed = value.trim();

        if (trimmed.length > 100) {
          return "Description must be at most 100 characters";
        }

        return "";
      }

      case "productSku": {
        const trimmed = value.trim();

        if (!trimmed) return "Product SKU is required";

        if (!/^[a-zA-Z0-9_-]{3,30}$/.test(trimmed)) {
          return "SKU must be 3–30 characters: letters, numbers, _ or - only";
        }

        return "";
      }

      case "productHsnsac": {
        const trimmed = value.trim();

        if (!trimmed) return "Product HSNSAC is required";

        if (!/^\d{4,8}$/.test(trimmed)) {
          return "HSNSAC must be a 4–8 digit number (no letters or symbols)";
        }

        return "";
      }

      case "productTaxper":
        return value && /^\d+(\.\d{1,2})?$/.test(value)
          ? ""
          : "Enter valid tax % (e.g., 5.0)";
      default:
        return "";
    }
  };

  // const handleBlur = (e) => {
  //   const { name, value } = e.target;
  //   setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  // };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (["orderAmount", "codAmount"].includes(name)) {
      const floatVal = parseFloat(value);
      if (!isNaN(floatVal)) {
        formattedValue = floatVal.toFixed(2);
      }
    }

    setForm((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, formattedValue, {
        ...form,
        [name]: formattedValue,
      }),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle Payment Mode logic
    if (name === "paymentMode") {
      const updatedForm = {
        ...form,
        paymentMode: value,
      };

      // Clear dependent fields based on new mode
      if (value === "PPD") {
        updatedForm.codAmount = "";
      } else if (value === "COD") {
        updatedForm.orderAmount = ""; // Optional reset for clarity
      } else if (value === "") {
        updatedForm.orderAmount = "";
        updatedForm.codAmount = "";
      }

      setForm(updatedForm);

      // Validate paymentMode + clear errors if needed
      setErrors((prev) => ({
        ...prev,
        paymentMode: validateField("paymentMode", value),
      }));

      return;
    }

    // ✅ Express Type logic should be handled here, not inside paymentMode condition
    // ✅ EXPRESS TYPE HANDLING
    if (name === "expressType") {
      let options = [];

      if (value === "air") {
        options = [
          { label: "Bluedart", value: "54" },
          { label: "Delhivery", value: "12" },
        ];
      } else if (value === "surface") {
        options = [
          { label: "Bluedart", value: "27" },
          { label: "Delhivery", value: "63" },
        ];
      }

      setCarrierOptions(options);

      setForm((prev) => ({
        ...prev,
        expressType: value,
        carrierId: "", // reset on change
      }));

      setErrors((prev) => ({
        ...prev,
        expressType: validateField("expressType", value),
      }));

      return;
    }
    // Generic case (other fields)
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value, {
        ...form,
        [name]: value,
      }),
    }));

    // Pincode trigger preserved ✅
    if (name === "consigneePincode" && value?.length >= 6) {
      fetchPincodeDetails(value);
    }
  };

  const handleProductChange = (index, e) => {
    const { name, value } = e.target;
    const updatedProducts = [...products];
    updatedProducts[index][name] = value;
    setProducts(updatedProducts);

    const updatedProductErrors = [...productErrors];
    updatedProductErrors[index] = {
      ...updatedProductErrors[index],
      [name]: validateProductField(name, value),
    };
    setProductErrors(updatedProductErrors);
  };

  const handleProductBlur = (index, e) => {
    const { name, value } = e.target;
    const updatedProducts = [...products];
    let formattedValue = value.trim();

    const isRepeated = /^(\d)\1{4,}(\.\d{1,2})?$/.test(formattedValue);
    const isTooLong = formattedValue.length > 12; // Optional: limit extreme lengths

    if (
      (name === "productValue" || name === "productTaxper") &&
      (isRepeated || isTooLong)
    ) {
      const updatedProductErrors = [...productErrors];
      updatedProductErrors[index] = {
        ...updatedProductErrors[index],
        [name]: "Invalid or repeated digits not allowed",
      };
      setProductErrors(updatedProductErrors);
      return;
    }

    if (name === "productValue") {
      const floatVal = parseFloat(formattedValue);
      if (!isNaN(floatVal)) {
        formattedValue = floatVal.toFixed(2);
      }
    }

    if (name === "productTaxper") {
      const floatVal = parseFloat(formattedValue);
      if (!isNaN(floatVal)) {
        formattedValue = floatVal.toFixed(1);
      }
    }

    updatedProducts[index][name] = formattedValue;
    setProducts(updatedProducts);

    const updatedProductErrors = [...productErrors];
    updatedProductErrors[index] = {
      ...updatedProductErrors[index],
      [name]: validateProductField(name, formattedValue),
    };
    setProductErrors(updatedProductErrors);
  };

  const addProduct = () => {
    setProducts([
      ...products,
      {
        productName: "",
        productQuantity: "",
        productValue: "",
        productCategory: "",
        productDescription: "",
        productSku: "",
        productHsnsac: "",
        productTaxper: "",
      },
    ]);
    setProductErrors([...productErrors, {}]);
  };

  const removeProduct = (index) => {
    setProducts(products.filter((_, i) => i !== index));
    setProductErrors(productErrors.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(form).forEach((key) => {
      newErrors[key] = validateField(key, form[key]);
    });

    const orderAmount = parseFloat(form.orderAmount || 0);
    const codAmount = parseFloat(form.codAmount || 0);

    // Only validate COD Amount if value exists
    if (form.paymentMode === "COD" && form.codAmount?.trim()) {
      if (!/^\d+(\.\d{1,2})?$/.test(form.codAmount)) {
        newErrors.codAmount = "Enter a valid COD amount (e.g., 100.00)";
      } else if (codAmount > orderAmount) {
        newErrors.codAmount = "COD Amount cannot exceed Order Amount";
      }
    }

    const newProductErrors = products.map((product) => {
      const productError = {};
      Object.keys(product).forEach((key) => {
        productError[key] = validateProductField(key, product[key]);
      });
      return productError;
    });

    setErrors(newErrors);
    setProductErrors(newProductErrors);

    if (products.length === 0) {
      return "At least one product is required";
    }

    return Object.values(newErrors).some((error) => error) ||
      newProductErrors.some((prod) =>
        Object.values(prod).some((error) => error)
      )
      ? "Please correct the errors in the form"
      : null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      CustomAlert({
        title: "Validation Error",
        text: validationError,
        icon: "error",
        confirmButtonText: "Okay",
      });
      return;
    }

    if (!courierServiceKey) {
      CustomAlert({
        title: "Error",
        text: "No courier service key available. Please select a valid courier.",
        icon: "error",
        confirmButtonText: "Okay",
      });
      return;
    }

    setLoading(true);

    const requestBody = {
      username: loggedInUserName,
      consignee_name: form.consigneeName,
      consignee_mobile: form.consigneeMobile,
      consignee_phone: form.consigneePhone,
      consignee_emailid: form.consigneeEmailId,
      consignee_address1: form.consigneeAddress1,
      consignee_pincode: form.consigneePincode,
      consignee_city: form.consigneeCity,
      consignee_state: form.consigneeState,
      consignee_country: form.consigneeCountry,
      invoice_number: form.invoiceNumber,
      payment_mode: form.paymentMode,
      express_type: form.expressType,
      is_ndd: form.isNdd,
      order_amount: isNaN(parseFloat(form.orderAmount))
        ? "0.00"
        : parseFloat(form.orderAmount).toFixed(2),
      // cod_amount: parseFloat(form.codAmount || 0).toFixed(2),
      cod_amount:
        form.paymentMode === "PPD" || !form.codAmount?.trim()
          ? "0"
          : parseFloat(form.codAmount).toFixed(0), // removes .00
      shipment_weight: form.shipmentWeight,
      shipment_length: form.shipmentLength,
      shipment_width: form.shipmentWidth,
      shipment_height: form.shipmentHeight,
      pick_address_id: form.pickAddressId,
      return_address_id: form.returnAddressId,
      seller_id: form.sellerId,
      carrier_id: form.carrierId,
      ewaybill_number: form.ewaybill_number,
      products: products.map((product) => ({
        product_name: product.productName,
        product_quantity: product.productQuantity,
        product_value: parseFloat(product.productValue || 0).toFixed(2),
        product_category: product.productCategory,
        product_description: product.productDescription,
        product_sku: product.productSku,
        product_hsnsac: product.productHsnsac,
        product_taxper: parseFloat(product.productTaxper || 0),
      })),
    };

    console.log("Request Body:", requestBody);

    try {
      const headers = {
        courierkey: courierServiceKey,
        "Content-Type": "application/json",
      };

      console.log("headers being sent:", {
        courierkey: courierServiceKey,
      });

      const response = await axiosPostApi(
        apiList.CREATE_ORDER,
        requestBody,
        headers
      );
      if (response?.data?.meta?.status === "success") {
        CustomAlert({
          title: "Success",
          text: response?.data?.meta?.message,
          icon: "success",
          confirmButtonText: "Okay",
        });
        setForm({
          consigneeName: "",
          consigneeMobile: "",
          consigneePhone: "",
          consigneeEmailId: "",
          consigneeAddress1: "",
          consigneePincode: "",
          consigneeCity: "",
          consigneeState: "",
          consigneeCountry: "",
          paymentMode: "",
          expressType: "",
          isNdd: "0",
          orderAmount: "",
          codAmount: "",
          invoiceNumber: "",
          shipmentWeight: "",
          shipmentLength: "",
          shipmentWidth: "",
          shipmentHeight: "",
          pickAddressId: "",
          returnAddressId: "",
          sellerId: "",
          carrier_id: "",
          ewaybill_number: "", // For Testing Purpose Only Passed
        });
        setProducts([
          {
            productName: "",
            productQuantity: "",
            productValue: "",
            productCategory: "",
            productDescription: "",
            productSku: "",
            productHsnsac: "",
            productTaxper: "",
          },
        ]);
        setErrors({});
        setProductErrors([{}]);
        if (typeof refreshTable === "function") {
          refreshTable(); // ✅ refresh OrderMaster table
        }

        handleClose();
      } else {
        CustomAlert({
          title: "Error",
          text:
            response?.data?.message ||
            "Failed to create order. Please try again.",
          icon: "error",
          confirmButtonText: "Okay",
        });
      }
    } catch (error) {
      console.error("Create Order API error:", error);
      CustomAlert({
        title: "Error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred while creating the order. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div className={styles["overlay-style"]}>
      <div
        className={styles["modal-style"]}
        onClick={(e) => e.stopPropagation()}
      >
        {/* <div className={styles["header-style"]}>
          <h5 className="modal-title main-title">Create New Order</h5>
        </div> */}
        <div className="relative flex justify-between items-center p-4 ">
          <h5 className="modal-title main-title">Create New Order</h5>
          <button
            onClick={handleClose}
            title="Close"
            className="absolute top-2 right-4 text-gray-500 hover:text-red-500 text-5xl leading-none focus:outline-none"
          >
            &times;
          </button>
        </div>

        <form>
          <div
            className={styles["product-container-style"]}
            style={{ backgroundColor: "#f9fafb" }}
          >
            <h2 className="modal-title main-title mb-4">Consignee Details</h2>

            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {/* Consignee Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consignee Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneeName"
                  value={form.consigneeName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee Name"
                />
                {errors.consigneeName && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.consigneeName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consignee Mobile <span className="text-red-500">*</span>
                </label>
                {/* Consignee Mobile */}
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneeMobile"
                  value={form.consigneeMobile}
                  onChange={(e) => {
                    const onlyDigits = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10); // Only digits, max 10
                    setForm((prev) => ({
                      ...prev,
                      consigneeMobile: onlyDigits,
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      consigneeMobile: validateField(
                        "consigneeMobile",
                        onlyDigits
                      ),
                    }));
                  }}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee Mobile"
                />
                {errors.consigneeMobile && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.consigneeMobile}
                  </p>
                )}
              </div>

              {/* Consignee Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consignee Phone
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneePhone"
                  value={form.consigneePhone}
                  onChange={(e) => {
                    const onlyDigits = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10); // only 10 digits
                    setForm((prev) => ({
                      ...prev,
                      consigneePhone: onlyDigits,
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      consigneePhone: validateField(
                        "consigneePhone",
                        onlyDigits
                      ),
                    }));
                  }}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee Phone"
                />
                {errors.consigneePhone && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.consigneePhone}
                  </p>
                )}
              </div>

              {/* Consignee Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consignee Email Id <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneeEmailId"
                  value={form.consigneeEmailId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee Email Id"
                />
                {errors.consigneeEmailId && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.consigneeEmailId}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consignee Address 1 <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneeAddress1"
                  value={form.consigneeAddress1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee Address 1"
                />
                {errors.consigneeAddress1 && (
                  <div className="text-sm text-red-600 mt-1">
                    {errors.consigneeAddress1}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Consignee Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    name="consigneePincode"
                    value={form.consigneePincode}
                    maxLength={6}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    placeholder="Enter Consignee Pincode"
                    disabled={pincodeLoading}
                  />
                  {pincodeLoading && (
                    <div className="text-sm text-gray-500 mt-1">
                      Fetching details...
                    </div>
                  )}
                  {errors.consigneePincode && (
                    <div className="text-sm text-red-600 mt-1">
                      {errors.consigneePincode}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Consignee City <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    name="consigneeCity"
                    value={form.consigneeCity}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter Consignee City"
                    disabled
                  />
                  {errors.consigneeCity && (
                    <div className="text-sm text-red-600 mt-1">
                      {errors.consigneeCity}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 mt-6">
              {/* State - Wider Now */}
              <div className="flex flex-col w-[255px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Consignee State <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneeState"
                  value={form.consigneeState}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee State"
                  disabled
                />
                {errors.consigneeState && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.consigneeState}
                  </span>
                )}
              </div>

              {/* Country - Wider Now */}
              <div className="flex flex-col w-[255px] ml-9">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Consignee Country <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="consigneeCountry"
                  value={form.consigneeCountry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Consignee Country"
                  disabled
                />
                {errors.consigneeCountry && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.consigneeCountry}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* --------------------------------Order Details----------------------------------- */}

          {/* 2 Section */}
          <div
            className={`${styles["product-container-style"]} pt-6`}
            style={{ backgroundColor: "#f9fafb" }}
          >
            <h2 className="modal-title main-title mb-4">Order Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              {/* Payment Mode */}

              <div className="flex flex-col w-[250px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Payment Mode <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="paymentMode"
                  value={form.paymentMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select Payment Mode</option>
                  <option value="COD">Cash On Delivery</option>
                  <option value="PPD">PPD</option>
                </select>
                {errors.paymentMode && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.paymentMode}
                  </span>
                )}
              </div>

              {/* Order Amount */}
              <div className="flex flex-col w-[150px] ml-9 ">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Order Amount <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full text-right px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="orderAmount"
                  value={form.orderAmount}
                  onChange={(e) => {
                    const input = e.target.value;
                    // Allow only numbers and up to one optional decimal point
                    if (/^\d*\.?\d{0,2}$/.test(input)) {
                      setForm((prev) => ({ ...prev, orderAmount: input }));
                      setErrors((prev) => ({
                        ...prev,
                        orderAmount: validateField("orderAmount", input, {
                          ...form,
                          orderAmount: input,
                        }),
                      }));
                    }
                  }}
                  onBlur={handleBlur}
                  placeholder="0.00"
                  disabled={!form.paymentMode}
                />
                {errors.orderAmount && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.orderAmount}
                  </span>
                )}
              </div>

              {/* COD Amount */}
              <div className="flex flex-col w-[150px] ">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  COD Amount
                </label>
                <input
                  className="w-full text-right px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="codAmount"
                  value={form.codAmount}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(input)) {
                      setForm((prev) => ({ ...prev, codAmount: input }));
                      setErrors((prev) => ({
                        ...prev,
                        codAmount: validateField("codAmount", input, {
                          ...form,
                          codAmount: input,
                        }),
                      }));
                    }
                  }}
                  onBlur={handleBlur}
                  placeholder="0.00"
                  disabled={!form.paymentMode || form.paymentMode === "PPD"}
                />
                {errors.codAmount && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.codAmount}
                  </span>
                )}
              </div>

              {/* Invoice Number */}
              <div className="flex flex-col w-[250px] -ml-14">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Invoice Number <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="invoiceNumber"
                  value={form.invoiceNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Invoice Number"
                />
                {errors.invoiceNumber && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.invoiceNumber}
                  </span>
                )}
              </div>

              <div className="flex flex-col w-[234px] -ml-8">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Express Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="expressType"
                  // value="surface"
                  // readOnly
                  // onBlur={handleBlur}
                  // onChange={() => {}} // optional no-op
                  value={form.expressType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select Express Type</option>
                  <option value="air">Air</option>
                  <option value="surface">Surface</option>

                  {/* <option value="express">Express</option> */}
                </select>
                {errors.expressType && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.expressType}
                  </span>
                )}
              </div>

              {/* Is NDD */}
              <div className="flex flex-col w-[180px] ">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Is NDD
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="isNdd"
                  value={form.isNdd}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                </select>
                {errors.isNdd && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.isNdd}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* -----------------------------Shipment Details-------------------------------------- */}

          <div
            className={styles["product-container-style"]}
            style={{ backgroundColor: "#f9fafb" }}
          >
            <h2 className="modal-title main-title mb-4">Shipment Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {/* Shipment Weight */}
              <div className="flex flex-col w-[250px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  shipment Weight <span className="text-red-500">*</span>
                </label>
                <input
                  name="shipmentWeight"
                  value={form.shipmentWeight}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(input)) {
                      setForm((prev) => ({ ...prev, shipmentWeight: input }));
                      setErrors((prev) => ({
                        ...prev,
                        shipmentWeight: validateField("shipmentWeight", input, {
                          ...form,
                          shipmentWeight: input,
                        }),
                      }));
                    }
                  }}
                  onBlur={handleBlur}
                  className="text-right px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter Shipment Weight"
                />

                {errors.shipmentWeight && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.shipmentWeight}
                  </span>
                )}
              </div>

              {/* Shipment Length */}
              <div className="flex flex-col w-[250px] ">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Shipment Length <span className="text-red-500">*</span>
                </label>
                <input
                  name="shipmentLength"
                  value={form.shipmentLength}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(input)) {
                      setForm((prev) => ({ ...prev, shipmentLength: input }));
                      setErrors((prev) => ({
                        ...prev,
                        shipmentLength: validateField("shipmentLength", input, {
                          ...form,
                          shipmentLength: input,
                        }),
                      }));
                    }
                  }}
                  onBlur={(e) => {
                    const input = e.target.value.trim();

                    // Protect against long or repeated digits
                    const isRepeated = /^(\d)\1{4,}(\.\d{1,2})?$/.test(input); // e.g. 11111.00
                    const isTooLong = input.length > 10; // Avoid formatting huge input

                    if (isRepeated || isTooLong) {
                      setErrors((prev) => ({
                        ...prev,
                        shipmentLength:
                          "Invalid or repeated digits not allowed",
                      }));
                      return;
                    }

                    const formatted =
                      input && !isNaN(input)
                        ? parseFloat(input).toFixed(2)
                        : "";

                    setForm((prev) => ({ ...prev, shipmentLength: formatted }));

                    setErrors((prev) => ({
                      ...prev,
                      shipmentLength: validateField(
                        "shipmentLength",
                        formatted,
                        {
                          ...form,
                          shipmentLength: formatted,
                        }
                      ),
                    }));
                  }}
                  placeholder="Enter Shipment Length"
                  className="text-right px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.shipmentLength && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.shipmentLength}
                  </span>
                )}
              </div>

              {/* Shipment Width */}
              <div className="flex flex-col  w-[250px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Shipment Width <span className="text-red-500">*</span>
                </label>
                <input
                  name="shipmentWidth"
                  value={form.shipmentWidth}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(input)) {
                      setForm((prev) => ({ ...prev, shipmentWidth: input }));
                      setErrors((prev) => ({
                        ...prev,
                        shipmentWidth: validateField("shipmentWidth", input, {
                          ...form,
                          shipmentWidth: input,
                        }),
                      }));
                    }
                  }}
                  onBlur={(e) => {
                    const input = e.target.value.trim();

                    const isRepeated = /^(\d)\1{4,}(\.\d{1,2})?$/.test(input);
                    const isTooLong = input.length > 10;

                    if (isRepeated || isTooLong) {
                      setErrors((prev) => ({
                        ...prev,
                        shipmentWidth: "Invalid or repeated digits not allowed",
                      }));
                      return;
                    }

                    const formatted =
                      input && !isNaN(input)
                        ? parseFloat(input).toFixed(2)
                        : "";

                    setForm((prev) => ({ ...prev, shipmentWidth: formatted }));
                    setErrors((prev) => ({
                      ...prev,
                      shipmentWidth: validateField("shipmentWidth", formatted, {
                        ...form,
                        shipmentWidth: formatted,
                      }),
                    }));
                  }}
                  className="text-right px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter Shipment Width"
                />
                {errors.shipmentWidth && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.shipmentWidth}
                  </span>
                )}
              </div>

              {/* Shipment Height */}
              <div className="flex flex-col  w-[250px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Shipment Height <span className="text-red-500">*</span>
                </label>
                <input
                  className="text-right px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="shipmentHeight"
                  value={form.shipmentHeight}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(input)) {
                      setForm((prev) => ({ ...prev, shipmentHeight: input }));
                      setErrors((prev) => ({
                        ...prev,
                        shipmentHeight: validateField("shipmentHeight", input, {
                          ...form,
                          shipmentHeight: input,
                        }),
                      }));
                    }
                  }}
                  onBlur={(e) => {
                    const input = e.target.value.trim();

                    const isRepeated = /^(\d)\1{4,}(\.\d{1,2})?$/.test(input);
                    const isTooLong = input.length > 10;

                    if (isRepeated || isTooLong) {
                      setErrors((prev) => ({
                        ...prev,
                        shipmentHeight:
                          "Invalid or repeated digits not allowed",
                      }));
                      return;
                    }

                    const formatted =
                      input && !isNaN(input)
                        ? parseFloat(input).toFixed(2)
                        : "";

                    setForm((prev) => ({ ...prev, shipmentHeight: formatted }));
                    setErrors((prev) => ({
                      ...prev,
                      shipmentHeight: validateField(
                        "shipmentHeight",
                        formatted,
                        {
                          ...form,
                          shipmentHeight: formatted,
                        }
                      ),
                    }));
                  }}
                  placeholder="Enter Shipment Height"
                />
                {errors.shipmentHeight && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.shipmentHeight}
                  </span>
                )}
              </div>

              {/* Pick Address Id */}
              <div className="flex flex-col  w-[250px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Pick Address Id <span className="text-red-500">*</span>
                </label>
                <input
                  className="px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="pickAddressId"
                  value={form.pickAddressId}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const input = e.target.value.trim();

                    const isRepeated = /^(\d)\1{4,}$/.test(input); // e.g. 11111
                    const isTooLong = input.length > 6;

                    if (isRepeated || isTooLong) {
                      setErrors((prev) => ({
                        ...prev,
                        pickAddressId: "Invalid or repeated digits not allowed",
                      }));
                      return;
                    }

                    const formatted = input.replace(/\D/g, ""); // Only digits
                    setForm((prev) => ({ ...prev, pickAddressId: formatted }));

                    setErrors((prev) => ({
                      ...prev,
                      pickAddressId: validateField("pickAddressId", formatted, {
                        ...form,
                        pickAddressId: formatted,
                      }),
                    }));
                  }}
                  placeholder="Enter Pick Address Id"
                />
                {errors.pickAddressId && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.pickAddressId}
                  </span>
                )}
              </div>

              {/* Return Address Id */}
              <div className="flex flex-col  w-[250px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Return Address Id <span className="text-red-500">*</span>
                </label>
                <input
                  className="px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="returnAddressId"
                  value={form.returnAddressId}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const input = e.target.value.trim();

                    const isRepeated = /^(\d)\1{4,}$/.test(input); // e.g., 11111 or longer
                    const isTooLong = input.length > 6;

                    if (isRepeated || isTooLong) {
                      setErrors((prev) => ({
                        ...prev,
                        returnAddressId:
                          "Invalid or repeated digits not allowed",
                      }));
                      return;
                    }

                    const formatted = input.replace(/\D/g, ""); // Remove non-digits

                    setForm((prev) => ({
                      ...prev,
                      returnAddressId: formatted,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      returnAddressId: validateField(
                        "returnAddressId",
                        formatted,
                        {
                          ...form,
                          returnAddressId: formatted,
                        }
                      ),
                    }));
                  }}
                  placeholder="Enter Return Address Id"
                />
                {errors.returnAddressId && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.returnAddressId}
                  </span>
                )}
              </div>

              {/* Carrier By */}
              <div className="flex flex-col w-[234px] -ml-8">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Courier By <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="carrierId"
                  value={form.carrierId}
                  onChange={handleChange}
                  disabled={!form.expressType}
                  onClick={() => {
                    if (!form.expressType) {
                      alert("Please select an Express Type first.");
                    }
                  }}
                >
                  <option value="">Select Courier By</option>
                  {carrierOptions.map((carrier) => (
                    <option key={carrier.value} value={carrier.value}>
                      {carrier.label}
                    </option>
                  ))}
                </select>

                {errors.carrierId && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.carrierId}
                  </span>
                )}
              </div>

              {/* Seller Id */}
              <div className="flex flex-col  w-[180px]">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Seller Id
                </label>
                <input
                  className="px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="sellerId"
                  value={form.sellerId}
                  onChange={handleChange}
                  onBlur={(e) => {
                    const input = e.target.value.trim();

                    if (!input) {
                      setForm((prev) => ({ ...prev, sellerId: "" }));
                      setErrors((prev) => ({
                        ...prev,
                        sellerId: "",
                      }));
                      return;
                    }

                    const isRepeated = /^(\d)\1{4,}$/.test(input); // 5+ same digits
                    const isTooLong = input.length > 6;

                    if (isRepeated || isTooLong) {
                      setErrors((prev) => ({
                        ...prev,
                        sellerId: "Invalid or repeated digits not allowed",
                      }));
                      return;
                    }

                    const formatted = input.replace(/\D/g, ""); // Remove non-digits
                    setForm((prev) => ({ ...prev, sellerId: formatted }));

                    setErrors((prev) => ({
                      ...prev,
                      sellerId: validateField("sellerId", formatted, {
                        ...form,
                        sellerId: formatted,
                      }),
                    }));
                  }}
                  placeholder="Enter Seller Id"
                />
                {errors.sellerId && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors.sellerId}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ----------------------------------Product Section ------------------------------------- */}

          {products.map((prod, index) => (
            <div
              key={index}
              className={styles["product-container-style"]}
              style={{ backgroundColor: "#f9fafb" }}
            >
              <h5 className="modal-title main-title mb-4">Products</h5>
              {/* Top Row - 3-column grid */}
              <div className={styles["product-grid-3"]}>
                {/* Product Name */}
                <div className={styles["product-field"]}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className={styles["input-style"]}
                    name="productName"
                    value={prod.productName}
                    onChange={(e) => handleProductChange(index, e)}
                    placeholder="Enter Product Name"
                    maxLength={50}
                    onBlur={(e) => handleProductBlur(index, e)}
                  />
                  {productErrors[index]?.productName && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productName}
                    </div>
                  )}
                </div>

                {/* Product Quantity */}
                <div className={styles["product-field"]}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Quantity{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-right placeholder:text-left"
                    name="productQuantity"
                    value={prod.productQuantity}
                    onChange={(e) => handleProductChange(index, e)}
                    onBlur={(e) => handleProductBlur(index, e)}
                    placeholder="Enter Quantity"
                  />
                  {productErrors[index]?.productQuantity && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productQuantity}
                    </div>
                  )}
                </div>

                {/* Product Value */}
                <div className={styles["product-field"]}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Value{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-right placeholder:text-left"
                    name="productValue"
                    value={prod.productValue}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (/^\d*\.?\d{0,2}$/.test(input)) {
                        const updatedProducts = [...products];
                        updatedProducts[index][e.target.name] = input;
                        setProducts(updatedProducts);

                        const updatedProductErrors = [...productErrors];
                        updatedProductErrors[index] = {
                          ...updatedProductErrors[index],
                          [e.target.name]: validateProductField(
                            e.target.name,
                            input
                          ),
                        };
                        setProductErrors(updatedProductErrors);
                      }
                    }}
                    onBlur={(e) => handleProductBlur(index, e)}
                    placeholder="Enter Value"
                  />
                  {productErrors[index]?.productValue && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productValue}
                    </div>
                  )}
                </div>
              </div>

              {/* Middle Row - 2-column grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                {/* Product Category */}
                <div className="flex flex-col  w-[250px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Category{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className={styles["input-style"]}
                    name="productCategory"
                    value={prod.productCategory}
                    onChange={(e) => handleProductChange(index, e)}
                    placeholder="Enter Category"
                    onBlur={(e) => handleProductBlur(index, e)}
                  />
                  {productErrors[index]?.productCategory && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productCategory}
                    </div>
                  )}
                </div>

                {/* Product Taxper */}
                <div className="flex flex-col  w-[250px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Taxper{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-right placeholder:text-left"
                    name="productTaxper"
                    value={prod.productTaxper}
                    onChange={(e) => handleProductChange(index, e)}
                    onBlur={(e) => handleProductBlur(index, e)}
                    placeholder="Enter Tax %"
                  />
                  {productErrors[index]?.productTaxper && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productTaxper}
                    </div>
                  )}
                </div>

                <div className="flex flex-col  w-[250px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product SKU{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className={styles["input-style"]}
                    name="productSku"
                    value={prod.productSku}
                    onChange={(e) => handleProductChange(index, e)}
                    placeholder="Enter SKU"
                    onBlur={(e) => handleProductBlur(index, e)}
                  />
                  {productErrors[index]?.productSku && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productSku}
                    </div>
                  )}
                </div>

                <div className="flex flex-col  w-[250px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product HSNSAC{" "}
                    <span className={styles["required-asterisk"]}>*</span>
                  </label>
                  <input
                    className={styles["input-style"]}
                    name="productHsnsac"
                    value={prod.productHsnsac}
                    onChange={(e) => handleProductChange(index, e)}
                    placeholder="Enter HSNSAC"
                    onBlur={(e) => handleProductBlur(index, e)}
                  />
                  {productErrors[index]?.productHsnsac && (
                    <div className={styles["error-style"]}>
                      {productErrors[index].productHsnsac}
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Row - SKU & HSNSAC side by side */}
              {/* <div className={styles["product-grid-2"]}>
                {/* Product SKU */}

              {/* Product HSNSAC */}

              {/* </div> */}

              {/* Full-width textarea for Product Description */}
              <div className={styles["product-description-section"]}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Description{" "}
                  <span className={styles["required-asterisk"]}>*</span>
                </label>

                <textarea
                  className={`${styles["input-style"]} ${styles["description-textarea"]}`}
                  name="productDescription"
                  value={prod.productDescription}
                  onChange={(e) => handleProductChange(index, e)}
                  placeholder="Enter Description"
                  onBlur={(e) => handleProductBlur(index, e)}
                />
                {productErrors[index]?.productDescription && (
                  <div className={styles["error-style"]}>
                    {productErrors[index].productDescription}
                  </div>
                )}
              </div>

              {/* Remove product button */}
              {products.length > 1 && (
                <button
                  type="button"
                  className={styles["remove-product-btn-style"]}
                  onClick={() => removeProduct(index)}
                  title="Remove Product"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className={styles["add-product-btn-style"]}
            onClick={addProduct}
          >
            + Add Product
          </button>

          <div className={styles["button-container"]}>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles["btn-primary"]}
              onClick={handleSubmit}
              disabled={loading || pincodeLoading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateOrderModal;
